import { Box, Button, Grid, LinearProgress, Tooltip, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOpenIncidentsRequest } from "../../../actions";
import { DispatchEventFeedModel, IRootReducer } from "../../../reducers";
import { ShortIdUtil } from "../../../utils/id-utils";
import IncidentCardSimple from "./incident-card-simple";
import PanelActionIcon from "../../common/panel-collapse-icon";

interface DashboardIncidentsProps {
  props: {
    isCollapsed?: boolean;
    isScreenLessThanLarge?: boolean;
    selectedIncId?: string;
    dispatchAddtlInfo: DispatchEventFeedModel;
    handleCollapse?: () => void;
  };
}

const DashboardIncidents: FC<DashboardIncidentsProps> = ({ props }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const openIncidentStore = useSelector((state: IRootReducer) => state.vccIncidentReducer.openIncidents);
  const jogFilters = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.jogFilters);

  const showLoadingAnimation = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 1250);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    showLoadingAnimation();
  }, [openIncidentStore, showLoadingAnimation]);

  const handleIncidentPaging = () => {
    dispatch(getOpenIncidentsRequest(jogFilters.join(","), true));
  }

  const getGridHeight = () => {    
    return `calc(100% - ${openIncidentStore.pagingKeys ? 40 : 0}px)`;
  }

  return (
    <Grid container style={{ height: "calc(100vh - 120px)" }}>      
      <Grid item style={{ height: "100%" }} xs={true}>
        <Box display="flex" justifyContent="center" width="100%">
          {props?.handleCollapse && !props.isScreenLessThanLarge && (
            <PanelActionIcon iconType="right" isActive={props?.isCollapsed} onClick={props.handleCollapse} />
          )}

          <Typography style={{ margin: "auto", textAlign: "center" }} width={"100%"} variant="h6">
            {props.isCollapsed ? "Incidents" : "Active VCC Incidents"}
          </Typography>
        </Box>
        {isLoading && (
          <Box m={1} width="100%">
            <LinearProgress />
          </Box>
        )}
        <Box
          pr={1}
          style={{
            height: getGridHeight(),
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {openIncidentStore.data?.length ? (
            openIncidentStore.data.map((incident, index) => (
              <Box mb={1} key={ShortIdUtil(incident.id)}>
                <IncidentCardSimple
                  key={incident.id}
                  props={{
                    isCollapsed: props.isCollapsed,
                    vccIncident: incident,
                    dispatchAddtlInfo: props.dispatchAddtlInfo,
                    index: index
                  }}
                />
              </Box>
            ))
          ) : (
            <Box m={1} display="flex" justifyContent="center">
              <Tooltip title={isLoading ? "" : "You may change your selected JOG(s) by clicking on the \"Edit selected JOG(s)\" icon above."}>
                <Typography variant="h6">
                  {isLoading ? "Loading..." : "There are currently no active VCC Incidents associated with your selected JOG(s)"}
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>
        {openIncidentStore.pagingKeys && (
          <Button variant="contained" color="inherit" size="small" onClick={handleIncidentPaging} style={{ float: "right", marginTop: "8px" }}>
            {props.isCollapsed ? "Load More" : "Load More Incidents"}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default DashboardIncidents;
